// tss-react bound to the MUI theme context; outside a ThemeProvider MUI's useTheme returns the default theme.
import { createElement, forwardRef } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider, Global } from '@emotion/react';
import { useTheme } from '@mui/material/styles';
import { createMakeAndWithStyles } from 'tss-react';
import { createCssAndCx } from 'tss-react/cssAndCx';

// tss rules get their own sheet, appended to <head> on first use.
// It follows MUI's prepended sheet, so a tss class wins an equal-specificity tie with a MUI default.
// Render-time tss inserts stay out of MUI's cache, so emotion places <GlobalStyles> and CssBaseline after the CSS files.
// Client (JSS) sets this meta so tss goes before its JSS sheets and a JSS parent override wins.
const insertionPoint = document.querySelector('meta[name="tss-insertion-point"]') ?? undefined;
const cache = createCache({ key: 'tss', insertionPoint });

const tss = createMakeAndWithStyles({ useTheme, cache });

export { keyframes } from 'tss-react';
export const { makeStyles } = tss;

// cx for withStyles components, which get no hook: it merges tss classes into one class, the later argument winning.
export const { cx } = createCssAndCx({ cache });

// Global rules in the tss cache: emotion places them just before the tss rules, so after MUI's prepended sheet.
// They win an equal-specificity tie with a MUI default, as JSS @global rules did, and lose one with a tss class.
export function GlobalStyles({ styles }) {
  const theme = useTheme();
  return createElement(
    CacheProvider,
    { value: cache },
    createElement(Global, { styles: typeof styles === 'function' ? styles(theme) : styles }),
  );
}

// tss withStyles passes className to the wrapped component even when it is undefined.
// A component that spreads its rest props after its own className would lose that className, so drop the undefined key.
export function withStyles(Component, styles, params) {
  if (typeof Component === 'string') {
    return tss.withStyles(Component, styles, params);
  }
  const Inner = forwardRef(function Inner({ className, ...props }, ref) {
    return createElement(Component, { ...props, ...(className === undefined ? {} : { className }), ...(ref ? { ref } : {}) });
  });
  Inner.displayName = Component.displayName || Component.name;
  return tss.withStyles(Inner, styles, params);
}

// In a class component, getClasses(this.props).root is the root class merged with the className prop, the later one winning.
withStyles.getClasses = tss.withStyles.getClasses;
